/**
 * GraphicShape - Imported SVG rendered as Konva.Image
 * Uses Image directly as the transformable element for reliable Transformer behavior
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Image, Group } from 'react-konva';
import Konva from 'konva';
import type { TemplateShape } from '@jigflow/shared';
import { CursorMode } from '../useTemplateStore';

interface GraphicShapeProps {
    shape: TemplateShape;
    isSelected: boolean;
    workAreaWidth: number;
    workAreaHeight: number;
    scale: number;
    cursorMode?: CursorMode;
    onSelect: () => void;
    onChange: (updates: Partial<TemplateShape>) => void;
    onDragMove?: (id: string, x: number, y: number, width: number, height: number, node: Konva.Node) => void;
    onDragEnd?: () => void;
    registerRef: (id: string, node: Konva.Node | null) => void;
}

export function GraphicShape({
    shape,
    isSelected,
    workAreaWidth: _workAreaWidth,
    workAreaHeight: _workAreaHeight,
    scale: _scale,
    cursorMode = 'move',
    onSelect,
    onChange,
    onDragMove,
    onDragEnd: onDragEndCallback,
    registerRef
}: GraphicShapeProps) {
    void _scale;
    void _workAreaWidth;
    void _workAreaHeight;
    const canDrag = cursorMode === 'move' && isSelected;
    const groupRef = useRef<Konva.Group>(null);
    const imageRef = useRef<Konva.Image>(null);
    const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);

    useEffect(() => {
        registerRef(shape.id, groupRef.current);
        return () => registerRef(shape.id, null);
    }, [shape.id, registerRef]);

    const svgDataUrl = useMemo(() => {
        if (!shape.svgData) return null;

        let modifiedSvg = shape.svgData;

        const engraveOn = shape.engrave !== false;
        const scoreOn = shape.score === true;
        const cutOn = shape.cut === true;
        const hasAnyLaserOp = scoreOn || engraveOn || cutOn;
        const allOpsOff = shape.engrave === false && !scoreOn && !cutOn;

        let css = '';

        if (allOpsOff) {
            css = `path, rect, circle, ellipse, polygon, line, polyline { fill: rgba(180, 180, 180, 0.4) !important; fill-rule: evenodd !important; stroke: rgba(150, 150, 150, 0.6) !important; stroke-width: 0.5 !important; }`;
        } else if (hasAnyLaserOp) {
            const fillStyle = engraveOn
                ? `fill: ${shape.engraveColor || '#000000'} !important; fill-rule: evenodd !important;`
                : `fill: none !important;`;

            let strokeRule = `stroke: none !important;`;
            if (scoreOn) {
                strokeRule = `stroke: ${shape.scoreColor || '#fe0002'} !important;`;
            } else if (cutOn) {
                strokeRule = `stroke: ${shape.cutColor || '#00befe'} !important;`;
            }

            css = `path, rect, circle, ellipse, polygon, line, polyline { ${fillStyle} ${strokeRule} }`;
        }

        const bgRule = 'svg { background: transparent; }';
        const fullCss = css ? `${bgRule} ${css}` : bgRule;
        modifiedSvg = modifiedSvg.replace(
            /(<svg[^>]*>)/,
            `$1<style>${fullCss}</style>`
        );

        const encoded = encodeURIComponent(modifiedSvg);
        return `data:image/svg+xml,${encoded}`;
    }, [shape.svgData, shape.score, shape.engrave, shape.cut,
        shape.scoreColor, shape.engraveColor, shape.cutColor]);

    useEffect(() => {
        if (!svgDataUrl) return;

        const img = new window.Image();
        img.onload = () => {
            setLoadedImage(img);
        };
        img.onerror = (err) => {
            console.error('[GraphicShape] Failed to load SVG:', err);
        };
        img.src = svgDataUrl;

        return () => {
            setLoadedImage(null);
        };
    }, [svgDataUrl]);

    const handleDragMoveInternal = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;
        if (onDragMove) {
            onDragMove(shape.id, node.x(), node.y(), shape.width, shape.height, node);
        }
    };

    const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;
        onChange({
            x: node.x(),
            y: node.y()
        });
        if (onDragEndCallback) {
            onDragEndCallback();
        }
    };

    const handleTransformEnd = (e: Konva.KonvaEventObject<Event>) => {
        const node = e.target as Konva.Group;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        const newWidth = Math.max(5, shape.width * scaleX);
        const newHeight = Math.max(5, shape.height * scaleY);

        node.scaleX(1);
        node.scaleY(1);

        onChange({
            x: node.x(),
            y: node.y(),
            width: newWidth,
            height: newHeight,
            rotation: node.rotation()
        });
    };

    if (!shape.svgData) return null;

    return (
        <Group
            ref={groupRef}
            x={shape.x}
            y={shape.y}
            rotation={shape.rotation || 0}
            draggable={canDrag}
            onClick={onSelect}
            onTap={onSelect}
            onDragMove={canDrag ? handleDragMoveInternal : undefined}
            onDragEnd={canDrag ? handleDragEnd : undefined}
            onTransformEnd={canDrag ? handleTransformEnd : undefined}
        >
            <Image
                ref={imageRef}
                image={loadedImage || undefined}
                x={0}
                y={0}
                width={shape.width}
                height={shape.height}
            />
        </Group>
    );
}
